import {
  Flex, FlexColumn,
  useIsUserAuth,
  UserRoles,
  useUserHasRole,
} from '@equifinance/shared';
import { useMemo, useState } from 'react';
import styled from 'styled-components';
import { Task } from '../../../contexts/tasks';
import { Notes } from './Notes';
import { Evidence } from './Evidence';

const TaskContentContainer = styled(FlexColumn)`${({ theme }) => `
  background: ${theme.colours.neutral[10]};
  border-radius: ${theme.borderRadius[2]};
  box-shadow: 0px 1px 3px 0px rgba(16, 24, 40, 0.10), 0px 1px 2px 0px rgba(16, 24, 40, 0.06);
`}`;

const TaskTabs = styled(Flex)`${({ theme }) => `
  padding: 0 ${theme.padding[5]};
  gap: ${theme.padding[3]};
  border-bottom: ${theme.borderWidth[1]} solid ${theme.colours.neutral[7]};
`}`;

const TaskTabItem = styled.div<{ isActive?: boolean }>`${({ theme, isActive }) => `
  padding: ${theme.padding[3]} 0;
  font-size: ${theme.fontSize[2]};
  font-weight: ${theme.fontWeight.bold};
  color: ${isActive ? theme.colours.primary[1] : theme.colours.neutral[3]};
  border-bottom: ${isActive ? `${theme.borderWidth[2]} solid ${theme.colours.primary[1]}` : 'none'};
  cursor: pointer;
`}`;

const ExternalNotes = styled(Flex)`${({ theme }) => `
  padding: ${theme.padding[4]} 0;
  justify-content: center;
  align-items: center;
`}`;

const EVIDENCE_TAB = 'Evidence';
const NOTES_TAB = 'Notes';
const TASK_TABS = [EVIDENCE_TAB, NOTES_TAB];

function TaskTabContent({ tab, task, taskIndex }: { tab: string, task: Task, taskIndex: number }) {
  const isBroker = useIsUserAuth();
  const isSalesSupport = useUserHasRole(UserRoles.SALES_SUPPORT);
  const isUnderwriter = useUserHasRole(UserRoles.UNDERWRITER);

  const canNotAddNote = useMemo(
    () => (
      (isUnderwriter && ['Approved', 'ApprovedWithException'].includes(task.status))
      || ((isBroker || isSalesSupport) && ['PendingSubmission', 'SubmittedToLender', 'Approved', 'ApprovedWithException'].includes(task.status))),
    [task.status, isUnderwriter, isSalesSupport, isBroker],
  );

  switch (tab) {
    case EVIDENCE_TAB: return <Evidence task={task} taskIndex={taskIndex} />;
    case NOTES_TAB: return (
      <ExternalNotes>
        <Notes
          taskId={task.id}
          notes={task?.notes ?? []}
          isExternalNote
          canAddNote={!canNotAddNote}
        />
      </ExternalNotes>
    );
    default: return <></>;
  }
}

export default function TaskContent({ task, taskIndex }: { task: Task, taskIndex: number }) {
  const [currentTab, setCurrentTab] = useState(TASK_TABS[0]);

  const changeToTab = (toTab: string) => {
    if (toTab === currentTab) return;
    setCurrentTab(toTab);
  };

  return (
    <TaskContentContainer>
      <TaskTabs>
        {TASK_TABS.map((tab) => (
          <TaskTabItem key={tab} onClick={() => changeToTab(tab)} isActive={currentTab === tab}>
            {tab}
            {tab === NOTES_TAB ? ` (${task?.notes?.length ?? '0'})` : ''}
          </TaskTabItem>
        ))}
      </TaskTabs>
      <TaskTabContent tab={currentTab} task={task} taskIndex={taskIndex} />
    </TaskContentContainer>
  );
}
